import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingAPI, demoAPI } from '../api';

/**
 * WelcomeCard — calm editorial intro for first-time users.
 *
 * Almost invisible. No tutorial checklist, no progress dots, no step cards.
 * One quiet editorial passage with two restrained CTAs.
 *
 * Copy is admin-customizable via Demo Experience → "welcome_intro" placement.
 *
 * Props:
 *   t, demoBoardId, lang, onDismiss, onCreate
 */
const WelcomeCard = ({
  t = {},
  demoBoardId,
  lang = 'en',
  onDismiss,
  onCreate,
}) => {
  const navigate = useNavigate();
  const [hiding, setHiding] = useState(false);
  const [copy, setCopy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    demoAPI.getGuidance()
      .then(res => {
        if (cancelled) return;
        const block = res.data?.guidance?.welcome_intro;
        if (block) setCopy(block[lang] || block.en);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lang]);

  const dismiss = async () => {
    setHiding(true);
    try { await onboardingAPI.dismissWelcome(); } catch (e) { /* non-fatal */ }
    if (onDismiss) onDismiss();
  };

  const openDemo = () => {
    if (demoBoardId) navigate(`/editor/${demoBoardId}`);
  };

  const createBoard = () => {
    if (onCreate) onCreate();
  };

  // Sensible fallback in case admin guidance hasn't loaded yet.
  const title = copy?.title || t.welcome_title || 'A private workspace for visual direction.';
  const body = copy?.body || 'Take a moment to explore the example board — it shows how a finished client experience feels.';

  return (
    <section
      data-testid="welcome-card"
      style={{
        padding: '72px 48px 64px',
        margin: '0 0 36px',
        borderBottom: '1px solid var(--kahla-border)',
        position: 'relative',
        opacity: hiding ? 0 : 1,
        transition: 'opacity 0.4s ease',
      }}
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="welcome-dismiss-btn"
        title="Dismiss"
        style={{
          position: 'absolute', top: 18, right: 18,
          background: 'none', border: 'none', padding: 8,
          fontSize: 16, lineHeight: 1, cursor: 'pointer',
          color: 'var(--kahla-text-light)', opacity: 0.4,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.4)}
      >×</button>

      <p style={{
        fontFamily: 'Playfair Display, serif',
        fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
        color: 'var(--kahla-text-light)', margin: 0, fontWeight: 300,
      }}>
        {t.welcome_eyebrow || 'Welcome to Maestra'}
      </p>

      <h2
        data-testid="welcome-title"
        style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 'clamp(28px, 4vw, 40px)',
          fontWeight: 400, color: 'var(--kahla-charcoal)',
          margin: '14px 0 18px', lineHeight: 1.18,
          letterSpacing: '-0.005em', maxWidth: 720,
        }}
      >
        {title}
      </h2>

      <p
        data-testid="welcome-body"
        style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic',
          fontSize: 18, lineHeight: 1.75,
          color: 'var(--kahla-text-light)',
          margin: '0 0 40px', maxWidth: 620,
        }}
      >
        {body}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
        {demoBoardId && (
          <button
            type="button"
            onClick={openDemo}
            data-testid="welcome-cta-demo"
            style={{
              background: 'var(--kahla-charcoal)',
              color: '#fff',
              border: 'none',
              padding: '12px 26px',
              fontFamily: 'inherit',
              fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.85)}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = 1)}
          >
            {t.welcome_cta_explore || 'Open the example board'}
          </button>
        )}
        <button
          type="button"
          onClick={createBoard}
          data-testid="welcome-cta-create"
          style={{
            background: 'none', border: 'none', padding: '4px 0',
            fontFamily: 'inherit',
            fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
            color: 'var(--kahla-charcoal)',
            textDecoration: 'underline',
            textUnderlineOffset: 5,
            cursor: 'pointer',
          }}
        >
          {t.welcome_cta_create || 'Begin your studio'}
        </button>
      </div>
    </section>
  );
};

export default WelcomeCard;
